// src/components/ValidationCotationCompare.jsx
// Vue INTERNE team/admin : lors d'une demande de validation, compare l'estimation
// faite par le client (verification_requests.data) avec la côte ARTKIV
// (RPC get_artwork_cotation : artist_quotes + Artprice). Fait ressortir l'écart pour
// que l'équipe juge — et recommande un expert si le calcul du client paraît anormal.
// La côte n'est JAMAIS montrée au client (il fait son estimation indépendante).
import React, { useEffect, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";

const GOLD = "#a9843f";
const BORDER = "#e5e0d8";

const eur = (n) => (n == null ? "—" : new Intl.NumberFormat("fr", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Math.round(Number(n))));
const usd = (n) => {
  const a = Number(n);
  if (n == null || !Number.isFinite(a)) return "—";
  if (a >= 1e6) return "$" + (a / 1e6).toFixed(1).replace(/\.0$/, "") + " M";
  if (a >= 1e3) return "$" + Math.round(a / 1e3) + " k";
  return "$" + Math.round(a);
};

// Extrait la valeur estimée par le client depuis un data jsonb de forme variable.
function extractClientValue(data) {
  if (!data || typeof data !== "object") return null;
  const KEYS = ["estimated_value", "final_value", "final_estimate", "value", "estimate", "central_value", "montant", "valeur", "result", "estimation", "price"];
  for (const k of KEYS) {
    const v = Number(data[k]);
    if (Number.isFinite(v) && v > 0) return v;
  }
  for (const key of Object.keys(data)) {
    const v = data[key];
    if (v && typeof v === "object") {
      const r = extractClientValue(v);
      if (r) return r;
    }
  }
  return null;
}

export default function ValidationCotationCompare({ artworkId, clientData }) {
  const { user } = useAuth();
  const [cote, setCote] = useState(undefined);
  const [expertVal, setExpertVal] = useState("");
  const [retainedVal, setRetainedVal] = useState("");
  const [writeMsg, setWriteMsg] = useState("");
  const [writing, setWriting] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!artworkId) { setCote(null); return; }
    (async () => {
      try {
        const { data, error } = await supabase.rpc("get_artwork_cotation", { p_artwork_id: artworkId });
        if (error) throw error;
        if (alive) setCote(data || { estimate: null, artprice: null });
      } catch {
        if (alive) setCote(null);
      }
    })();
    return () => { alive = false; };
  }, [artworkId]);

  const clientValue = extractClientValue(clientData);
  const est = cote && cote.estimate ? cote.estimate : null;
  const art = cote && cote.artprice ? cote.artprice : null;

  // Écart client vs central ARTKIV.
  let gapPct = null;
  if (clientValue != null && est && est.central != null && Number(est.central) > 0) {
    gapPct = Math.round(((clientValue - Number(est.central)) / Number(est.central)) * 100);
  }
  const gapAbs = gapPct == null ? null : Math.abs(gapPct);
  const gapColor = gapAbs == null ? "#8a8378" : gapAbs <= 25 ? "#2e7d32" : gapAbs <= 50 ? "#a9843f" : "#b3261e";
  const gapVerdict =
    gapAbs == null ? "" : gapAbs <= 25 ? "cohérent" : gapAbs <= 50 ? "écart notable — vérifier la méthode" : "écart fort — recommander un expert";

  const parseNum = (v) => { const n = Number(String(v ?? "").replace(/\s/g, "").replace(",", ".")); return Number.isFinite(n) ? n : null; };
  // Écrit une valeur dans l'historique (artwork_valuation_history). RLS : staff autorisé.
  const writeValue = async (value_type, payload) => {
    if (!user?.id || !artworkId) return;
    setWriting(true); setWriteMsg("");
    try {
      const { error } = await supabase.from("artwork_valuation_history").insert({
        artwork_id: artworkId, value_type, currency: "EUR",
        valuation_date: new Date().toISOString().slice(0, 10),
        created_by: user.id, ...payload,
      });
      if (error) throw error;
      setWriteMsg("✓ Inscrit dans l'historique des valeurs");
    } catch (e) {
      setWriteMsg("Erreur : " + (e?.message || "insertion"));
    } finally {
      setWriting(false);
    }
  };

  const box = { border: `1px solid ${BORDER}`, borderRadius: 10, padding: "10px 12px", background: "#fff" };
  const lbl = { fontSize: 11, letterSpacing: ".04em", textTransform: "uppercase", color: "#8a8378" };
  const val = { fontSize: 18, fontWeight: 700, color: "#1a1a1a", fontFamily: "Georgia, serif" };

  return (
    <div style={{ border: `1px solid ${BORDER}`, borderRadius: 12, padding: 16, background: "#faf6ec", marginTop: 16 }}>
      <div style={{ fontSize: 11, letterSpacing: ".16em", textTransform: "uppercase", color: GOLD, marginBottom: 10 }}>
        Comparaison interne ARTKIV · non visible du client
      </div>

      {cote === undefined ? (
        <p style={{ fontSize: 13, color: "#8a8378", margin: 0 }}>Chargement de la côte…</p>
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
            <div style={box}>
              <div style={lbl}>Estimation du client</div>
              <div style={val}>{clientValue != null ? eur(clientValue) : "non détectée"}</div>
              {clientValue == null && <div style={{ fontSize: 11, color: "#b8b1a4", marginTop: 2 }}>voir le JSON ci-dessus</div>}
            </div>
            <div style={box}>
              <div style={lbl}>Côte ARTKIV</div>
              <div style={val}>
                {est
                  ? est.low != null && est.high != null && Number(est.low) !== Number(est.high)
                    ? `${eur(est.low)} – ${eur(est.high)}`
                    : eur(est.central)
                  : "aucune cote"}
              </div>
              {est && est.central != null && <div style={{ fontSize: 11, color: "#8a8378", marginTop: 2 }}>central ~ {eur(est.central)} · {est.count} cote(s)</div>}
            </div>
            <div style={box}>
              <div style={lbl}>Écart</div>
              <div style={{ ...val, color: gapColor }}>{gapPct == null ? "—" : (gapPct > 0 ? "+" : "") + gapPct + " %"}</div>
              {gapVerdict && <div style={{ fontSize: 11, color: gapColor, marginTop: 2 }}>{gapVerdict}</div>}
            </div>
          </div>

          {art && (
            <div style={{ marginTop: 10, fontSize: 12.5, color: "#6b6459" }}>
              Repère marché : <span style={{ color: GOLD, fontWeight: 600 }}>Artprice #{art.rank}</span>
              {art.avg_sold_usd != null && <> · prix moyen adjugé ~{usd(art.avg_sold_usd)}</>}
              {art.best_result_usd != null && <> · plus haut {usd(art.best_result_usd)}</>}
              <span style={{ color: "#b8b1a4" }}> (niveau artiste, USD)</span>
            </div>
          )}

          {!est && !art && (
            <p style={{ fontSize: 12.5, color: "#8a8378", margin: "8px 0 0" }}>
              Aucune côte ARTKIV pour cet artiste — comparez manuellement, ou recommandez un expert.
            </p>
          )}

          {/* Inscrire dans l'historique des valeurs (§P3) — écriture équipe sur validation */}
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${BORDER}` }}>
            <div style={{ fontSize: 11, letterSpacing: ".08em", textTransform: "uppercase", color: GOLD, marginBottom: 8 }}>
              Inscrire dans l'historique des valeurs
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              {est && (
                <button type="button" disabled={writing}
                  onClick={() => writeValue("algorithmic", { amount_min: est.low, amount_max: est.high, amount_retained: est.central, source_type: "artprice", source_name: "Côte ARTKIV", method: "comparables", confidence_level: "medium" })}
                  style={{ fontSize: 12.5, padding: "7px 12px", borderRadius: 8, border: `1px solid ${BORDER}`, background: "#fff", cursor: "pointer" }}>
                  Notre côte → algorithmique
                </button>
              )}
              <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                <input type="number" placeholder="Valeur d'expertise (€)" value={expertVal} onChange={(e) => setExpertVal(e.target.value)}
                  style={{ fontSize: 12.5, padding: "6px 8px", border: `1px solid ${BORDER}`, borderRadius: 6, width: 150 }} />
                <button type="button" disabled={writing || parseNum(expertVal) == null}
                  onClick={() => { const v = parseNum(expertVal); if (v != null) writeValue("expert", { amount_min: v, amount_retained: v, source_type: "expert", source_name: "Validation ARTKIV" }); }}
                  style={{ fontSize: 12.5, padding: "7px 12px", borderRadius: 8, border: `1px solid ${BORDER}`, background: "#fff", cursor: "pointer" }}>
                  Expertise
                </button>
              </span>
              <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                <input type="number" placeholder="Valeur retenue (€)" value={retainedVal} onChange={(e) => setRetainedVal(e.target.value)}
                  style={{ fontSize: 12.5, padding: "6px 8px", border: `1px solid ${BORDER}`, borderRadius: 6, width: 150 }} />
                <button type="button" disabled={writing || parseNum(retainedVal) == null}
                  onClick={() => { const v = parseNum(retainedVal); if (v != null) writeValue("retained", { amount_min: v, amount_retained: v, source_type: "validation", source_name: "Validation ARTKIV" }); }}
                  style={{ fontSize: 12.5, padding: "7px 12px", borderRadius: 8, border: "none", background: "#1a1a1a", color: "#f9f7f4", cursor: "pointer", fontWeight: 600 }}>
                  Valeur retenue
                </button>
              </span>
            </div>
            {writeMsg && <div style={{ fontSize: 12, color: writeMsg.startsWith("✓") ? "#2e7d32" : "#b3261e", marginTop: 8 }}>{writeMsg}</div>}
          </div>

          <div style={{ fontSize: 11.5, color: "#b8b1a4", marginTop: 10 }}>
            Repère d'aide à la validation (arithmétique). L'équipe juge la méthode du client ; en cas d'écart anormal, signaler et recommander un expert.
          </div>
        </>
      )}
    </div>
  );
}
